'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/ui/shadcn/card';
import { Progress } from '@/shared/ui/shadcn/progress';
import { Badge } from '@/shared/ui/shadcn/badge';
import { cn } from '@/shared/lib/cn';
import { Loader2 } from 'lucide-react';
import {
  UpscaleStatus,
  type UpscaleStatusType,
} from '@/shared/constants/status';

interface UpscaleJob {
  renderId: string;
  status: UpscaleStatusType;
  progress: number;
}

interface UpscaleProgressProps {
  jobs: UpscaleJob[];
  className?: string;
}

export function UpscaleProgress({
  jobs,
  className = '',
}: UpscaleProgressProps) {
  const tUpscale = useTranslations('upscaleProgress');

  if (jobs.length === 0) {
    return null;
  }

  const activeJobs = jobs.filter(
    (job) =>
      job.status === UpscaleStatus.PROCESSING ||
      job.status === UpscaleStatus.PENDING
  );
  const completedJobs = jobs.filter(
    (job) => job.status === UpscaleStatus.COMPLETED
  );
  const failedJobs = jobs.filter((job) => job.status === UpscaleStatus.FAILED);

  if (
    activeJobs.length === 0 &&
    completedJobs.length === 0 &&
    failedJobs.length === 0
  ) {
    return null;
  }

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-form-label">{tUpscale('title')}</CardTitle>
          <div className="flex items-center gap-2">
            {completedJobs.length > 0 && (
              <Badge
                variant="outline"
                className="text-primary border-primary/20"
              >
                {tUpscale('completedCount', { count: completedJobs.length })}
              </Badge>
            )}
            {failedJobs.length > 0 && (
              <Badge variant="destructive">
                {tUpscale('failedCount', { count: failedJobs.length })}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {activeJobs.map((job) => (
          <div key={job.renderId} className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-progress-meta text-muted-foreground">
                {tUpscale('renderShort', { id: job.renderId.slice(-8) })}
              </span>
              <span className="text-progress-value">{Math.round(job.progress)}%</span>
            </div>
            <Progress value={job.progress} className="h-1.5" />
          </div>
        ))}

        {activeJobs.length > 0 && (
          <div className="flex items-center text-progress-meta text-muted-foreground">
            <Loader2 className="h-3 w-3 mr-1.5 animate-spin text-primary" />
            <span>
              {tUpscale('processingCount', { count: activeJobs.length })}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
